/**
 * The observations window: a modal Win94 dialog with a 2-column grid of
 * discovery cards. Discovered entries get a deterministic doodle stamp;
 * undiscovered entries show a dark silhouette, '???' and their hint.
 * All display strings arrive from the caller.
 */
import { hashString, mulberry32 } from './glyphs';

export interface LogEntry {
  id: string;
  name: string;
  hint: string;
  discovered: boolean;
  timesSeen: number;
}

const MONO = "Consolas, 'Courier New', monospace";
const STAMP_GRID = 14;
const STAMP_CELL = 2; // 14 cells x 2px = 28x28 stamp
const STAMP_BG = '#E4D8B4';
const STAMP_INK = '#241C12';

/**
 * 28x28 deterministic doodle stamp from the entry id: strokes like the
 * glyph alphabet but denser, dark on beige.
 */
function stampCanvas(id: string): HTMLCanvasElement {
  const rand = mulberry32(hashString(`stamp:${id}`));
  const g = new Uint8Array(STAMP_GRID * STAMP_GRID);
  const ri = (lo: number, hi: number): number => lo + Math.floor(rand() * (hi - lo + 1));
  const cl = (v: number): number => Math.max(0, Math.min(STAMP_GRID - 1, v));
  const set = (x: number, y: number): void => {
    if (x >= 0 && x < STAMP_GRID && y >= 0 && y < STAMP_GRID) g[y * STAMP_GRID + x] = 1;
  };
  const line = (x0: number, y0: number, x1: number, y1: number): void => {
    let x = x0;
    let y = y0;
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      set(x, y);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y += sy;
      }
    }
  };

  // one enclosing box for structure...
  const w = ri(4, 8);
  const h = ri(4, 8);
  const bx = ri(1, STAMP_GRID - 2 - w);
  const by = ri(1, STAMP_GRID - 2 - h);
  line(bx, by, bx + w, by);
  line(bx + w, by, bx + w, by + h);
  line(bx + w, by + h, bx, by + h);
  line(bx, by + h, bx, by);

  // ...then a dense scatter of strokes
  const extras = ri(5, 7);
  for (let i = 0; i < extras; i++) {
    switch (ri(0, 3)) {
      case 0: {
        const y = ri(1, STAMP_GRID - 2);
        const x0 = ri(1, STAMP_GRID - 6);
        line(x0, y, cl(x0 + ri(3, 6)), y);
        break;
      }
      case 1: {
        const x = ri(1, STAMP_GRID - 2);
        const y0 = ri(1, STAMP_GRID - 6);
        line(x, y0, x, cl(y0 + ri(3, 6)));
        break;
      }
      case 2: {
        const x0 = ri(1, STAMP_GRID - 6);
        const y0 = ri(2, STAMP_GRID - 3);
        const len = ri(3, 5);
        const dir = rand() < 0.5 ? 1 : -1;
        line(x0, y0, cl(x0 + len), cl(y0 + dir * len));
        break;
      }
      default:
        set(ri(2, STAMP_GRID - 3), ri(2, STAMP_GRID - 3));
        break;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = STAMP_GRID * STAMP_CELL;
  canvas.height = STAMP_GRID * STAMP_CELL;
  canvas.style.cssText = `width:${STAMP_GRID * STAMP_CELL}px;height:${STAMP_GRID * STAMP_CELL}px;image-rendering:pixelated;display:block;`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = STAMP_BG;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = STAMP_INK;
  for (let y = 0; y < STAMP_GRID; y++) {
    for (let x = 0; x < STAMP_GRID; x++) {
      if (g[y * STAMP_GRID + x]) {
        ctx.fillRect(x * STAMP_CELL, y * STAMP_CELL, STAMP_CELL, STAMP_CELL);
      }
    }
  }
  return canvas;
}

function entryCard(entry: LogEntry): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'w94-sunken';
  el.style.cssText = 'padding:6px;display:flex;flex-direction:column;gap:4px;min-width:0;';
  if (entry.discovered) {
    const name = document.createElement('div');
    name.style.cssText = `font:bold 11px/1.3 ${MONO};color:var(--w94-text);max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
    name.textContent = entry.name;
    const seen = document.createElement('div');
    seen.style.cssText = `font:9px/1.3 ${MONO};color:var(--w94-text);opacity:.8;`;
    seen.textContent = `SEEN x${entry.timesSeen}`;
    el.append(stampCanvas(entry.id), name, seen);
  } else {
    const silhouette = document.createElement('div');
    silhouette.style.cssText = 'width:28px;height:28px;background:#241C12;';
    const unknown = document.createElement('div');
    unknown.style.cssText = `font:bold 11px/1.3 ${MONO};color:var(--w94-text);`;
    unknown.textContent = '???';
    const hint = document.createElement('div');
    hint.style.cssText = `font:9px/1.4 ${MONO};color:var(--w94-text);opacity:.6;`;
    hint.textContent = entry.hint;
    el.append(silhouette, unknown, hint);
  }
  return el;
}

export function showLogbook(parent: HTMLElement, title: string, entries: LogEntry[]): void {
  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'position:fixed',
    'inset:0',
    'display:grid',
    'place-items:center',
    'z-index:40',
    'pointer-events:auto',
    'background:rgba(0,0,0,0.25)',
  ].join(';');

  const win = document.createElement('div');
  win.className = 'w94-raised';
  win.style.cssText = 'width:min(92vw,360px);padding:2px;box-sizing:border-box;';

  const bar = document.createElement('div');
  bar.textContent = title;
  bar.style.cssText = [
    'background:linear-gradient(90deg,var(--w94-title-a),var(--w94-title-b))',
    'color:#fff',
    'font:bold 12px/1 monospace',
    'padding:5px 8px',
    'letter-spacing:1px',
  ].join(';');

  const body = document.createElement('div');
  body.style.cssText = [
    'max-height:60vh',
    'overflow-y:auto',
    'padding:10px',
    'display:grid',
    'grid-template-columns:1fr 1fr',
    'gap:6px',
    'pointer-events:auto',
    'touch-action:pan-y',
    'overscroll-behavior:contain',
    '-webkit-overflow-scrolling:touch',
  ].join(';');
  for (const entry of entries) {
    body.appendChild(entryCard(entry));
  }

  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display:flex;justify-content:center;padding:6px 0 12px;';
  const ok = document.createElement('button');
  ok.type = 'button';
  ok.className = 'w94-btn';
  ok.textContent = 'OK';
  ok.style.cssText = 'min-width:96px;height:44px;pointer-events:auto;';
  ok.addEventListener('click', () => wrap.remove());
  buttonRow.appendChild(ok);

  win.append(bar, body, buttonRow);
  wrap.appendChild(win);
  parent.appendChild(wrap);
}
